/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Typography,
} from '@material-ui/core';
import AdminTabs from '../../util/tabs/AdminTabs';
import api from '../../../../utils/api';
import './SetGames.scss';
import {  getDateFormattedString } from '../../../../utils/functions';

function apiListToPublished(list = []) {
  return (list || []).map((item, index) => ({
    id: item.id,
    name: item.name != null ? item.name : item.game,
    searchLightActive: item.searchLightActive !== false,
    sort: index,
  }));
}

function publishedToApiList(publishedList) {
  const sorted = publishedList
    .slice()
    .sort((a, b) => {
      if (a.searchLightActive !== b.searchLightActive) return a.searchLightActive ? -1 : 1;
      return a.sort - b.sort;
    });
  return sorted.map((g) => ({
    id: g.id,
    name: g.name,
    searchLightActive: g.searchLightActive !== false,
  }));
}

function SetGames(props) {
  const { auth, admin = {}, getAGameList } = props;

  // 游戏库使用项目已有接口：getAGameList (GET /api/v1/admin/games/) → admin.games.data
  const gamesFromApi = admin.games?.data || [];
  const gamesLoading = admin.games?.isLoading === true;

  const [publishedGames, setPublishedGames] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detailGameId, setDetailGameId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [likeData, setLikeData] = useState({});

  const loadLikes = useCallback(() => {
    const token = auth?.token;
    if (!token) return;
    api.getSearchLightGameLikes(token).subscribe(
      (res) => {
        const raw = res?.response?.data;
        const next =
          raw?.likes && typeof raw.likes === 'object'
            ? raw.likes
            : typeof raw === 'object' && raw !== null
              ? raw
              : {};
        const normalized = {};
        Object.keys(next).forEach((gameId) => {
          const list = Array.isArray(next[gameId]) ? next[gameId] : [];
          normalized[gameId] = list.map((item) => ({
            username: item.username || item.userName || '-',
            time: item.time || item.likedAt || '',
          }));
        });
        setLikeData(normalized);
      },
      () => setLikeData({}),
    );
  }, [auth?.token]);

  const loadSearchLightList = useCallback(() => {
    const token = auth?.token;
    if (!token) return;
    setListLoading(true);
    api.getSearchLightGamesAdmin(token).subscribe(
      (res) => {
        const raw = res?.response?.data;
        const list = Array.isArray(raw?.games) ? raw.games : Array.isArray(raw) ? raw : [];
        setPublishedGames(apiListToPublished(list));
        setListLoading(false);
      },
      () => {
        setListLoading(false);
        setPublishedGames([]);
      },
    );
  }, [auth?.token]);

  const loadFromApi = useCallback(() => {
    const token = auth?.token;
    if (!token) return;
    getAGameList(token);
    loadSearchLightList();
    loadLikes();
  }, [auth?.token, getAGameList, loadSearchLightList, loadLikes]);

  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  const savePublished = (next) => {
    setPublishedGames(next);
  };

  // 将当前列表同步到后端（payload: id, name, searchLightActive）
  const persistList = useCallback(
    (list) => {
      const token = auth?.token;
      if (!token) return;
      const payload = publishedToApiList(list);
      api.updateSearchLightGames(payload, token).subscribe(
        () => {},
        () => {
          setSnackbarMessage('Save failed');
          setSnackbarOpen(true);
        },
      );
    },
    [auth?.token],
  );

  const toggleSelect = (gameId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  };

  const addSelectedToPublished = () => {
    if (selectedIds.size === 0) {
      setSnackbarMessage('Please select at least one game to add.');
      setSnackbarOpen(true);
      return;
    }
    const published = [...publishedGames];
    let added = 0;
    selectedIds.forEach((gameId) => {
      if (published.some((g) => g.id === gameId)) return;
      const game = gamesFromApi.find((g) => g.id === gameId);
      if (game) {
        published.push({
          id: game.id,
          name: game.name,
          searchLightActive: true,
          sort: published.filter((g) => g.searchLightActive).length,
        });
        added += 1;
      }
    });
    savePublished(published);
    setSelectedIds(new Set());
    if (added > 0) {
      setSnackbarMessage('Games added successfully.');
      setSnackbarOpen(true);
      persistList(published);
    }
  };

  const toggleSearchLightActive = (gameId) => {
    const next = publishedGames.map((g) => {
      if (g.id !== gameId) return g;
      const nextActive = !g.searchLightActive;
      const activeCount = publishedGames.filter((x) => x.searchLightActive && x.id !== gameId).length;
      return {
        ...g,
        searchLightActive: nextActive,
        sort: nextActive ? activeCount : g.sort,
      };
    });
    savePublished(next);
    persistList(next);
  };

  const deletePublished = (gameId) => {
    setGameToDelete(gameId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (gameToDelete == null) return;
    const next = publishedGames.filter((g) => g.id !== gameToDelete);
    savePublished(next);
    setConfirmOpen(false);
    setGameToDelete(null);
    persistList(next);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setGameToDelete(null);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const getActiveSorted = () => {
    const active = publishedGames.filter((g) => g.searchLightActive).sort((a, b) => a.sort - b.sort);
    const inactive = publishedGames.filter((g) => !g.searchLightActive).sort((a, b) => a.sort - b.sort);
    return [...active, ...inactive];
  };

  const moveToTop = (gameId) => {
    const active = publishedGames.filter((g) => g.searchLightActive).sort((a, b) => a.sort - b.sort);
    const idx = active.findIndex((g) => g.id === gameId);
    if (idx <= 0) return;
    const next = publishedGames.map((g) => {
      if (!g.searchLightActive) return g;
      const i = active.findIndex((x) => x.id === g.id);
      if (i < idx) return { ...g, sort: g.sort + 1 };
      if (g.id === gameId) return { ...g, sort: 0 };
      return g;
    });
    savePublished(next);
    persistList(next);
  };

  const moveToBottom = (gameId) => {
    const active = publishedGames.filter((g) => g.searchLightActive).sort((a, b) => a.sort - b.sort);
    const idx = active.findIndex((g) => g.id === gameId);
    if (idx < 0 || idx >= active.length - 1) return;
    const maxSort = active.length - 1;
    const next = publishedGames.map((g) => {
      if (!g.searchLightActive) return g;
      const i = active.findIndex((x) => x.id === g.id);
      if (i > idx) return { ...g, sort: g.sort - 1 };
      if (g.id === gameId) return { ...g, sort: maxSort };
      return g;
    });
    savePublished(next);
    persistList(next);
  };

  const moveUp = (gameId) => {
    const active = publishedGames.filter((g) => g.searchLightActive).sort((a, b) => a.sort - b.sort);
    const idx = active.findIndex((g) => g.id === gameId);
    if (idx <= 0) return;
    const prev = active[idx - 1];
    const next = publishedGames.map((g) => {
      if (g.id === gameId) return { ...g, sort: prev.sort };
      if (g.id === prev.id) return { ...g, sort: active[idx].sort };
      return g;
    });
    savePublished(next);
    persistList(next);
  };

  const moveDown = (gameId) => {
    const active = publishedGames.filter((g) => g.searchLightActive).sort((a, b) => a.sort - b.sort);
    const idx = active.findIndex((g) => g.id === gameId);
    if (idx < 0 || idx >= active.length - 1) return;
    const nextGame = active[idx + 1];
    const next = publishedGames.map((g) => {
      if (g.id === gameId) return { ...g, sort: nextGame.sort };
      if (g.id === nextGame.id) return { ...g, sort: active[idx].sort };
      return g;
    });
    savePublished(next);
    persistList(next);
  };

  const openDetail = (gameId) => {
    setDetailGameId(gameId);
    setModalOpen(true);
  };

  const filteredLibrary = searchText.trim()
    ? gamesFromApi.filter((g) =>
        (g.name || '').toLowerCase().includes(searchText.toLowerCase().trim()),
      )
    : gamesFromApi;

  const sortedPublished = getActiveSorted();
  const activeList = sortedPublished.filter((g) => g.searchLightActive);

  return (
    <section className="AGameSet">
      <section className="AGameSet__wrapper">
        <AdminTabs {...props} />
        <section className="AGameSet__content">
          <h2 className="AGameSet__title">SET GAMES</h2>
          <div className="AGameSet__actions">
            <Link to="/admin/games" activeClassName="active" href="/admin/games">
              <button>Back</button>
            </Link>
          </div>
          <div className="AGameSet__container">
            <div className="AGameSet__sidebar">
              <h5 className="AGameSet__sidebar-title">
                <i className="fa fa-list me-2" aria-hidden="true" />
                Game Library
              </h5>
              <div className="AGameSet__search">
                <input
                  type="text"
                  className="AGameSet__input"
                  placeholder="Search games..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <button type="button" className="AGameSet__btn AGameSet__btn-outline">
                  <i className="fa fa-search" aria-hidden="true" />
                </button>
              </div>
              <div className="AGameSet__library">
                {gamesLoading ? (
                  <div className="AGameSet__loading">Loading games...</div>
                ) : (
                  filteredLibrary.map((game) => (
                    <button
                      type="button"
                      key={game.id}
                      className={`AGameSet__game-item ${selectedIds.has(game.id) ? 'selected' : ''}`}
                      onClick={() => toggleSelect(game.id)}
                    >
                      {game.name}
                    </button>
                  ))
                )}
              </div>
              <button type="button" className="AGameSet__btn AGameSet__btn-add" onClick={addSelectedToPublished}>
                <i className="fa fa-plus me-2" aria-hidden="true" />
                Add selected to published list
              </button>
            </div>

            <div className="AGameSet__main">
              <div className="AGameSet__card">
                <div className="AGameSet__card-header">
                  <h5 className="AGameSet__card-title">
                    <i className="fa fa-thumbtack me-2" aria-hidden="true" />
                    Games on SearchLight page
                  </h5>
                  <button
                    type="button"
                    className="AGameSet__btn AGameSet__btn-sm AGameSet__btn-outline"
                    onClick={loadFromApi}
                    disabled={gamesLoading || listLoading}
                  >
                    <i className="fa fa-sync-alt me-1" aria-hidden="true" />
                    Refresh
                  </button>
                </div>
                <div className="AGameSet__card-body">
                  <div className="AGameSet__table-wrap">
                    <table className="AGameSet__table">
                      <thead>
                        <tr>
                          <th>Game Name</th>
                          <th style={{ width: 120 }}>On SearchLight</th>
                          <th style={{ width: 100 }}>Likes</th>
                          <th className="AGameSet__action-col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPublished.map((game) => {
                          const likes = likeData[game.id] || [];
                          const likeCount = likes.length;
                          const isActive = game.searchLightActive !== false;
                          const activeIndex = activeList.findIndex((g) => g.id === game.id);
                          const isFirst = activeIndex === 0;
                          const isLast = activeIndex === activeList.length - 1;
                          return (
                            <tr key={game.id} className={!isActive ? 'AGameSet__row-inactive' : ''}>
                              <td className="AGameSet__name-cell">{game.name}</td>
                              <td>
                                <span className={`AGameSet__badge ${isActive ? 'AGameSet__badge-success' : 'AGameSet__badge-secondary'}`}>
                                  {isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>{likeCount}</td>
                              <td className="AGameSet__action-col">
                                <div className="AGameSet__actions-row">
                                  <button type="button" className="AGameSet__btn AGameSet__btn-sm AGameSet__btn-outline-primary" onClick={() => openDetail(game.id)}>
                                    <i className="fa fa-eye me-1" aria-hidden="true" />
                                    Detail
                                  </button>
                                  <button type="button" className="AGameSet__btn AGameSet__btn-sm AGameSet__btn-outline-warning" onClick={() => toggleSearchLightActive(game.id)}>
                                    <i className={`fa fa-toggle-${isActive ? 'on' : 'off'} me-1`} aria-hidden="true" />
                                    {isActive ? 'Inactive' : 'Active'}
                                  </button>
                                  <button type="button" className="AGameSet__btn AGameSet__btn-sm AGameSet__btn-outline-danger" onClick={() => deletePublished(game.id)}>
                                    <i className="fa fa-trash me-1" aria-hidden="true" />
                                    Remove
                                  </button>
                                  {isActive && (
                                    <div className="AGameSet__sort-group">
                                      <button type="button" className="AGameSet__sort-btn" title="Top" disabled={isFirst} onClick={() => moveToTop(game.id)}>
                                        <i className="fa fa-arrow-up" aria-hidden="true" />
                                      </button>
                                      <button type="button" className="AGameSet__sort-btn" title="Move up" disabled={isFirst} onClick={() => moveUp(game.id)}>
                                        <i className="fa fa-chevron-up" aria-hidden="true" />
                                      </button>
                                      <button type="button" className="AGameSet__sort-btn" title="Move down" disabled={isLast} onClick={() => moveDown(game.id)}>
                                        <i className="fa fa-chevron-down" aria-hidden="true" />
                                      </button>
                                      <button type="button" className="AGameSet__sort-btn" title="Bottom" disabled={isLast} onClick={() => moveToBottom(game.id)}>
                                        <i className="fa fa-arrow-down" aria-hidden="true" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="AGameSet__confirm-title"
      >
        <DialogTitle id="AGameSet__confirm-title">Remove Game</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this game from the published list?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {detailGameId != null && (() => {
        const game = publishedGames.find((g) => g.id === detailGameId);
        const likes = likeData[detailGameId] || [];
        if (!game) return null;
        return (
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            fullWidth
            maxWidth="sm"
            aria-labelledby="AGameSet__modal-title"
            classes={{ paper: 'AGameSet__detail-dialog' }}
          >
            <DialogTitle id="AGameSet__modal-title" className="AGameSet__detail-dialog-title">
              Game Detail&nbsp;-&nbsp;{game.name}
            </DialogTitle>
            <DialogContent dividers className="AGameSet__detail-dialog-content">
              <div className="AGameSet__detail-section">
                <Typography variant="subtitle1" component="h6" gutterBottom>
                  Basic Info
                </Typography>
                <Typography variant="body2" component="p">
                  <strong>Game ID:</strong> {game.id}
                </Typography>
                <Typography variant="body2" component="p">
                  <strong>On SearchLight:</strong>{' '}
                  <span
                    className={`AGameSet__badge ${
                      game.searchLightActive !== false
                        ? 'AGameSet__badge-success'
                        : 'AGameSet__badge-secondary'
                    }`}
                  >
                    {game.searchLightActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </Typography>
              </div>

              <hr className="AGameSet__hr" />

              <div className="AGameSet__detail-section">
                <Typography variant="subtitle1" component="h6" gutterBottom>
                  Users who liked ({likes.length})
                </Typography>
                <div className="AGameSet__table-wrap AGameSet__likes-table-wrap AGameSet__likes-table-scroll">
                  <table className="AGameSet__table AGameSet__table-sm">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Liked at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {likes.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="AGameSet__text-muted">
                            No likes yet
                          </td>
                        </tr>
                      ) : (
                        likes.map((like, i) => (
                          <tr key={i}>
                            <td>{like.username}</td>
                            <td>{getDateFormattedString(like.time, 'YYYY/MM/DD HH:mm:ss')}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </DialogContent>
            <DialogActions className="AGameSet__detail-dialog-actions">
              <Button onClick={() => setModalOpen(false)} color="primary" variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={snackbarMessage}
      />
    </section>
  );
}

SetGames.propTypes = {};

export default SetGames;
